import { useCallback, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { DEAL_BUILDER_QUERY_PARAM_PROGRESS, DEAL_BUILDER_QUERY_PARAM_STEP } from '../constants';
import { getNextStep, getPrevStep, isStepUnlocked } from '../lib';
import { DealBuilderStep } from '../types';

const isDealBuilderStep = (value: string | null): value is DealBuilderStep =>
  !!value && (Object.values(DealBuilderStep) as string[]).includes(value);

export interface DealBuilderStepNavigation {
  viewStep: DealBuilderStep;
  progressStep: DealBuilderStep;
  goToStep: (step: DealBuilderStep) => void;
  advanceToNextStep: () => void;
  goToPrevStep: () => void;
}

// Keeps the current/furthest step in the URL (search params) so refresh/back restores it
export const useDealBuilderStepNavigation = (): DealBuilderStepNavigation => {
  const location = useLocation();
  const { replace } = useHistory();

  const queryParams = new URLSearchParams(location.search);
  const stepParam = queryParams.get(DEAL_BUILDER_QUERY_PARAM_STEP);
  const progressParam = queryParams.get(DEAL_BUILDER_QUERY_PARAM_PROGRESS);

  const progressStep = isDealBuilderStep(progressParam) ? progressParam : DealBuilderStep.Contacts;
  const viewStep = isDealBuilderStep(stepParam) && isStepUnlocked(stepParam, progressStep) ? stepParam : progressStep;

  const setParams = useCallback(
    (nextViewStep: DealBuilderStep, nextProgressStep: DealBuilderStep) => {
      const nextQueryParams = new URLSearchParams(location.search);
      nextQueryParams.set(DEAL_BUILDER_QUERY_PARAM_STEP, nextViewStep);
      nextQueryParams.set(DEAL_BUILDER_QUERY_PARAM_PROGRESS, nextProgressStep);

      replace({ pathname: location.pathname, hash: location.hash, search: nextQueryParams.toString() });
    },
    [location.hash, location.pathname, location.search, replace]
  );

  useEffect(() => {
    if (!stepParam || !progressParam) {
      setParams(viewStep, progressStep);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepParam, progressParam]);

  const goToStep = useCallback(
    (step: DealBuilderStep) => {
      if (!isStepUnlocked(step, progressStep)) return;
      setParams(step, progressStep);
    },
    [progressStep, setParams]
  );

  const advanceToNextStep = useCallback(() => {
    const nextStep = getNextStep(viewStep);
    const nextProgressStep = isStepUnlocked(nextStep, progressStep) ? progressStep : nextStep;
    setParams(nextStep, nextProgressStep);
  }, [progressStep, setParams, viewStep]);

  const goToPrevStep = useCallback(() => {
    setParams(getPrevStep(viewStep), progressStep);
  }, [progressStep, setParams, viewStep]);

  return { viewStep, progressStep, goToStep, advanceToNextStep, goToPrevStep };
};
